/**
 * <ChatPanel /> — the right-side AI Chat Panel (M22 → M25).
 *
 * Toggled with ⌘/ from the AppShell. 320px on desktop, full sheet on
 * mobile, 0 width when closed. Stays mounted while closed so state
 * survives toggling + navigation within the session.
 *
 * M25 turns the single conversation into MULTIPLE parallel
 * conversations (M25.4): the panel holds a list of `Conversation`s,
 * one is active, and the <ThreadSwitcher /> in the header moves
 * between them, starts new ones, or reopens a pinned thread
 * (M25.3). Each conversation keeps its own message history +
 * ephemeral session id; a streaming turn patches the conversation
 * it was started in (captured at send time), so switching threads
 * mid-stream never cross-writes.
 *
 * Chat stays ephemeral by default (commandment 27) — the only
 * persistence is an explicit pin via <PinThreadButton />.
 */

import { Search, X } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";
import type { ChatThread } from "../../lib/api";
import { captureRichChatContext } from "../../lib/chat-context-rich";
import {
  CHAT_EXPLAIN_EVENT,
  CHAT_OPEN_THREAD_EVENT,
  type ChatExplainDetail,
  type ChatOpenThreadDetail,
} from "../../lib/chat-explain";
import { streamChat } from "../../lib/chat-streaming";
import type { ChatHistoryTurn } from "../../lib/chat-streaming";
import { useOptionalLegalEntityContext } from "../../lib/legalEntity";
import { AIBadge } from "../ui/AIBadge";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";
import { PinThreadButton } from "./PinThreadButton";
import { SuggestedActions } from "./SuggestedActions";
import { ThreadSwitcher } from "./ThreadSwitcher";
import type { ChatConfirmation, ChatTurn } from "./types";

let _turnSeq = 0;
function _turnId(): string {
  _turnSeq += 1;
  return `turn-${Date.now()}-${_turnSeq}`;
}

let _convoSeq = 0;
function _convoId(): string {
  _convoSeq += 1;
  return `convo-${Date.now()}-${_convoSeq}`;
}

// User-resizable chat panel width — drag the handle on the start
// edge to resize. Persisted in localStorage so the choice sticks.
const _CHAT_WIDTH_KEY = "solvait-aihr.chat-width";
const _CHAT_WIDTH_MIN = 320;
const _CHAT_WIDTH_MAX = 720;
const _CHAT_WIDTH_DEFAULT = 380;

function _initialChatWidth(): number {
  try {
    const saved = Number(localStorage.getItem(_CHAT_WIDTH_KEY));
    if (
      Number.isFinite(saved) &&
      saved >= _CHAT_WIDTH_MIN &&
      saved <= _CHAT_WIDTH_MAX
    ) {
      return saved;
    }
  } catch {
    /* localStorage unavailable */
  }
  return _CHAT_WIDTH_DEFAULT;
}

/**
 * Thin drag handle on the chat panel's start (workspace-facing)
 * edge — mousedown + drag resizes the panel; the choice persists.
 * The handle's clickable area is wider (6px) than its visible line.
 */
function ChatResizeHandle({
  onResize,
}: {
  onResize: (next: number) => void;
}) {
  const { t } = useTranslation();
  function onMouseDown(e: React.MouseEvent) {
    e.preventDefault();
    const isRtl = document.documentElement.dir === "rtl";
    function onMove(ev: MouseEvent) {
      const vw = window.innerWidth;
      const raw = isRtl ? ev.clientX : vw - ev.clientX;
      const clamped = Math.max(
        _CHAT_WIDTH_MIN,
        Math.min(_CHAT_WIDTH_MAX, raw),
      );
      onResize(clamped);
    }
    function onUp() {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";
  }
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("app.chat_resize_aria")}
      onMouseDown={onMouseDown}
      className="absolute inset-y-0 start-0 z-10 hidden w-1.5 -ms-0.5 cursor-ew-resize hover:bg-brand-blue/30 active:bg-brand-blue/50 lg:block"
    />
  );
}

/** One parallel conversation (M25.4). */
interface Conversation {
  id: string;
  title: string;
  messages: ChatTurn[];
  sessionId: string | null;
  /** Set once the conversation has been pinned to a saved thread. */
  pinnedThreadId?: string;
}

function _newConversation(): Conversation {
  return {
    id: _convoId(),
    title: "New chat",
    messages: [],
    sessionId: null,
  };
}

function _blankTurn(role: "user" | "assistant"): ChatTurn {
  return {
    id: _turnId(),
    role,
    createdAt: Date.now(),
    text: "",
    toolCalls: [],
    results: [],
    toolErrors: [],
    confirmations: [],
    notices: [],
    streaming: role === "assistant",
  };
}

/** "10:42 AM" — the time-divider label above a user turn group. */
function _timeLabel(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ChatPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { i18n, t } = useTranslation();
  const le = useOptionalLegalEntityContext();
  const queryClient = useQueryClient();
  // React 18 doesn't accept `inert` as a known prop on HTMLElements, so
  // set it imperatively via ref. When the panel is closed but kept in
  // layout (lg:flex 0-width column), `inert` removes its subtree from
  // both the focus order and the accessibility tree — fixing axe's
  // `aria-hidden-focus` violation. Move to a JSX prop once we upgrade
  // to React 19.
  const asideRef = useRef<HTMLElement | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>(
    () => [_newConversation()],
  );
  const [activeId, setActiveId] = useState<string>(
    () => conversations[0].id,
  );
  const [streaming, setStreaming] = useState(false);
  // M25.6 — in-session history search over the active conversation.
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  // User-resizable chat width (desktop only) — drag handle on the
  // start edge. Persists across sessions.
  const [chatWidth, setChatWidth] = useState<number>(_initialChatWidth);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Persist the resized width.
  useEffect(() => {
    try {
      localStorage.setItem(_CHAT_WIDTH_KEY, String(chatWidth));
    } catch {
      /* localStorage unavailable */
    }
  }, [chatWidth]);

  // Publish the docked panel's width as a CSS var so the AppShell
  // top bar + flex row can shrink to make room (the panel is a
  // full-height column on desktop, not a floater over content).
  useEffect(() => {
    const isDesktop =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(min-width: 1024px)").matches;
    const value = open && isDesktop ? `${chatWidth}px` : "0px";
    document.documentElement.style.setProperty(
      "--chat-panel-width",
      value,
    );
    return () => {
      // On unmount leave the var at the closed/0 state.
      document.documentElement.style.setProperty(
        "--chat-panel-width",
        "0px",
      );
    };
  }, [open, chatWidth]);

  const activeConvo =
    conversations.find((c) => c.id === activeId) ?? conversations[0];

  // Filtered view of the active conversation when a search is on.
  const query = search.trim().toLowerCase();
  const visibleMessages = query
    ? activeConvo.messages.filter((m) =>
        m.text.toLowerCase().includes(query),
      )
    : activeConvo.messages;

  // Auto-scroll to the newest content of the active conversation.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeConvo.messages]);

  // --- conversation patching ---------------------------------------------

  function patchConvo(
    convoId: string,
    fn: (c: Conversation) => Conversation,
  ) {
    setConversations((prev) =>
      prev.map((c) => (c.id === convoId ? fn(c) : c)),
    );
  }

  /** Patch the last assistant turn of a specific conversation. */
  function patchLastAssistant(
    convoId: string,
    fn: (t: ChatTurn) => ChatTurn,
  ) {
    patchConvo(convoId, (c) => {
      const msgs = [...c.messages];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === "assistant") {
          msgs[i] = fn(msgs[i]);
          break;
        }
      }
      return { ...c, messages: msgs };
    });
  }

  /** Patch one parked confirmation by id, wherever it lives. */
  function patchConfirmation(
    confirmationId: string,
    fn: (c: ChatConfirmation) => ChatConfirmation,
  ) {
    setConversations((prev) =>
      prev.map((convo) => ({
        ...convo,
        messages: convo.messages.map((turn) =>
          turn.confirmations.some(
            (c) => c.confirmationId === confirmationId,
          )
            ? {
                ...turn,
                confirmations: turn.confirmations.map((c) =>
                  c.confirmationId === confirmationId ? fn(c) : c,
                ),
              }
            : turn,
        ),
      })),
    );
  }

  async function applyConfirmation(confirmationId: string) {
    patchConfirmation(confirmationId, (c) => ({
      ...c,
      status: "applying",
      errorMessage: undefined,
    }));
    try {
      const res = await api.applyChatConfirmation(confirmationId);
      patchConfirmation(confirmationId, (c) => ({
        ...c,
        status: "applied",
        resultSummary: res.summary,
      }));
    } catch (e) {
      patchConfirmation(confirmationId, (c) => ({
        ...c,
        status: "error",
        errorMessage:
          (e as Error)?.message ?? "Couldn't apply this change.",
      }));
    }
  }

  async function cancelConfirmation(confirmationId: string) {
    try {
      await api.cancelChatConfirmation(confirmationId);
    } catch {
      // A cancel that fails (already resolved) still ends the card.
    }
    patchConfirmation(confirmationId, (c) => ({
      ...c,
      status: "cancelled",
    }));
  }

  // --- thread management (M25.3 / M25.4) ---------------------------------

  function newConversation() {
    const convo = _newConversation();
    setConversations((prev) => [...prev, convo]);
    setActiveId(convo.id);
  }

  async function openPinnedThread(threadId: string) {
    // Already open? Just activate it.
    const existing = conversations.find(
      (c) => c.pinnedThreadId === threadId,
    );
    if (existing) {
      setActiveId(existing.id);
      return;
    }
    try {
      const thread = await api.getChatThread(threadId);
      const convo: Conversation = {
        id: _convoId(),
        title: thread.name,
        messages: (thread.transcript as unknown as ChatTurn[]).map(
          (t) => ({
            ...t,
            streaming: false,
            createdAt: t.createdAt ?? Date.now(),
          }),
        ),
        sessionId: null,
        pinnedThreadId: thread.id,
      };
      setConversations((prev) => [...prev, convo]);
      setActiveId(convo.id);
    } catch {
      // Couldn't load — leave the user where they are.
    }
  }

  function handlePinned(thread: ChatThread) {
    // Stamp the active conversation as pinned + adopt the name.
    patchConvo(activeId, (c) => ({
      ...c,
      pinnedThreadId: thread.id,
      title: thread.name,
    }));
    void queryClient.invalidateQueries({
      queryKey: ["chat-threads"],
    });
  }

  // --- send --------------------------------------------------------------

  async function send(text: string) {
    if (streaming) return;
    // Capture the conversation this turn belongs to — switching
    // threads mid-stream must never cross-write.
    const convoId = activeId;
    const convo =
      conversations.find((c) => c.id === convoId) ?? activeConvo;

    const history: ChatHistoryTurn[] = convo.messages
      .filter((m) => m.text.trim().length > 0)
      .map((m) => ({ role: m.role, content: m.text }));

    const userTurn = { ..._blankTurn("user"), text };
    const assistantTurn = _blankTurn("assistant");
    patchConvo(convoId, (c) => ({
      ...c,
      messages: [...c.messages, userTurn, assistantTurn],
      // First user message names an untitled conversation.
      title:
        c.title === "New chat" && c.messages.length === 0
          ? text.slice(0, 40)
          : c.title,
    }));
    setStreaming(true);

    // Accumulated assistant text — used to seed the M25.5 follow-up
    // suggestions once the turn settles.
    let assistantText = "";

    const pageContext = captureRichChatContext();
    try {
      await streamChat(
        {
          message: text,
          history,
          locale: i18n.language === "ar" ? "ar" : "en",
          current_url: pageContext.current_url,
          current_entity_id: pageContext.current_entity_id,
          page_context: pageContext,
          session_id: convo.sessionId,
          // When scope is ALL_ENTITIES, send null so chat tools
          // don't enforce a single-LE filter. Otherwise send the
          // active LE id so query_contracts / query_employees /
          // query_workflows scope correctly and never leak
          // cross-LE data into a single-scope view.
          legal_entity_id: le?.isAllScope ? null : le?.legalEntityId || null,
        },
        (event) => {
          switch (event.type) {
            case "session":
              patchConvo(convoId, (c) => ({
                ...c,
                sessionId: event.session_id,
              }));
              break;
            case "text":
              assistantText += event.text;
              patchLastAssistant(convoId, (t) => ({
                ...t,
                text: t.text + event.text,
              }));
              break;
            case "tool_call":
              patchLastAssistant(convoId, (t) => ({
                ...t,
                toolCalls: [...t.toolCalls, event.name],
              }));
              break;
            case "tool_result":
              patchLastAssistant(convoId, (t) => ({
                ...t,
                results: [
                  ...t.results,
                  {
                    name: event.name,
                    render: event.render,
                    summary: event.summary,
                    data: event.data,
                  },
                ],
              }));
              break;
            case "tool_error":
              patchLastAssistant(convoId, (t) => ({
                ...t,
                toolErrors: [
                  ...t.toolErrors,
                  { name: event.name, message: event.message },
                ],
              }));
              break;
            case "confirmation":
              patchLastAssistant(convoId, (t) => ({
                ...t,
                confirmations: [
                  ...t.confirmations,
                  {
                    confirmationId: event.confirmation_id,
                    toolName: event.tool_name,
                    preview: event.preview,
                    status: "pending",
                  },
                ],
              }));
              break;
            case "notice":
              patchLastAssistant(convoId, (t) => ({
                ...t,
                notices: [...t.notices, event.message],
              }));
              break;
            case "error":
              patchLastAssistant(convoId, (t) => ({
                ...t,
                error: event.message,
              }));
              break;
            case "done":
              patchConvo(convoId, (c) => ({
                ...c,
                sessionId: event.session_id,
              }));
              break;
          }
        },
      );
    } finally {
      setStreaming(false);
      patchLastAssistant(convoId, (t) => ({
        ...t,
        streaming: false,
      }));
    }

    // M25.5 — suggest follow-ups once the turn has settled. Best-
    // effort: a failure or an empty list just shows no chips.
    if (assistantText.trim()) {
      void api
        .chatFollowUps({
          user_message: text,
          assistant_message: assistantText,
          locale: i18n.language === "ar" ? "ar" : "en",
          // Same all-entities → null rule as the chat-messages call
          // above. Follow-ups should be scoped consistently with the
          // turn they're proposing follow-ups for.
          legal_entity_id: le?.isAllScope ? null : le?.legalEntityId || null,
        })
        .then((res) => {
          if (res.follow_ups.length > 0) {
            patchLastAssistant(convoId, (t) => ({
              ...t,
              followUps: res.follow_ups,
            }));
          }
        })
        .catch(() => {
          /* follow-ups are best-effort */
        });
    }
  }

  // Chat-panel window events (M25.2 / M25.4). Refs keep the
  // once-registered listeners from going stale against the
  // closures they call.
  const sendRef = useRef<(text: string) => void>(() => {});
  sendRef.current = (text: string) => void send(text);
  const openThreadRef = useRef<(threadId: string) => void>(() => {});
  openThreadRef.current = (threadId: string) =>
    void openPinnedThread(threadId);
  useEffect(() => {
    function onExplain(e: Event) {
      const detail = (e as CustomEvent<ChatExplainDetail>).detail;
      if (detail?.prompt) sendRef.current(detail.prompt);
    }
    function onOpenThread(e: Event) {
      const detail = (e as CustomEvent<ChatOpenThreadDetail>).detail;
      if (detail?.threadId) openThreadRef.current(detail.threadId);
    }
    window.addEventListener(CHAT_EXPLAIN_EVENT, onExplain);
    window.addEventListener(CHAT_OPEN_THREAD_EVENT, onOpenThread);
    return () => {
      window.removeEventListener(CHAT_EXPLAIN_EVENT, onExplain);
      window.removeEventListener(
        CHAT_OPEN_THREAD_EVENT,
        onOpenThread,
      );
    };
  }, []);

  // The panel DOCKS, it doesn't float (Phase 2):
  //   - Desktop (≥lg): an in-flow column inside the AppShell flex
  //     row. Its width animates 0 ⇄ 320px; the workspace `<main>`
  //     (flex-1) reflows to fill the rest. Sticky like the sidebar.
  //   - Mobile (<lg): a full sheet below the top bar.
  //   - Closed: `hidden` on mobile; a 0-width flex column on
  //     desktop (stays mounted so the conversation survives).
  // RTL: it's the last flex child, so `flex` puts it on the left;
  // `border-s` keeps the divider on the workspace-facing edge.
  return (
    <aside
      ref={(el) => {
        asideRef.current = el;
        if (el) {
          // Imperative `inert` toggle (see asideRef declaration for why).
          if (open) {
            el.removeAttribute("inert");
          } else {
            el.setAttribute("inert", "");
          }
        }
      }}
      aria-label="AI assistant"
      aria-hidden={!open}
      data-chat-panel
      style={
        {
          "--chat-w": `${chatWidth}px`,
          backgroundColor: "white",
          borderInlineStart: "1px solid #e2e8f0",
          boxShadow: open
            ? "-4px 0 16px rgba(0, 0, 0, 0.04)"
            : "none",
        } as React.CSSProperties
      }
      className={[
        "flex flex-col",
        // Mobile: full sheet below the top bar, or gone.
        open
          ? "fixed inset-0 top-[var(--layout-topbar-height)] z-40 lg:static lg:inset-auto lg:top-auto lg:z-auto"
          : "hidden lg:flex",
        // M3X.WORKSPACE-CLOSURE Session 2.7 PR 2 — desktop chat is now
        // a REAL flex child of the workspace row. NO position:fixed
        // (was the overlay defect). Main content reflows via
        // `flex: 1` when chat's width transitions 0 → 320px.
        "lg:relative lg:shrink-0 lg:overflow-hidden",
        "lg:transition-[width] lg:duration-small lg:ease-out",
        open ? "lg:w-[var(--chat-w)]" : "lg:w-0",
      ].join(" ")}
    >
      {/* Fixed-width inner shell — keeps its layout while the
          aside's width animates, so content reveals rather than
          reflows. Full-width on the mobile sheet. The resize
          handle on the start edge lets the user widen / narrow it. */}
      <div className="relative flex h-full w-full flex-col lg:w-[var(--chat-w)]">
        <ChatResizeHandle onResize={setChatWidth} />
      {/* Header — "Ask Solvait" title row + a thin controls row. */}
      <header className="flex flex-col gap-1.5 border-b border-border-subtle px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5">
            <AIBadge />
            <span className="flex flex-col leading-tight">
              <span className="font-en-display text-sm font-semibold text-text-primary">
                {t("app.chat_ask_solvait")}
              </span>
              <span className="text-xs text-text-tertiary">
                {t("app.chat_subtitle")}
              </span>
            </span>
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("app.chat_close_aria")}
            className="rounded-md p-1 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex items-center justify-between gap-1">
          <ThreadSwitcher
            conversations={conversations.map((c) => ({
              id: c.id,
              title: c.title,
            }))}
            activeId={activeId}
            onSelect={setActiveId}
            onNew={newConversation}
            onOpenPinned={(id) => void openPinnedThread(id)}
          />
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => {
                setSearchOpen((o) => !o);
                if (searchOpen) setSearch("");
              }}
              aria-label={t("app.chat_search_conv_aria")}
              aria-pressed={searchOpen}
              className={`rounded-md p-1 hover:bg-surface-sunken hover:text-text-primary ${
                searchOpen
                  ? "text-text-primary"
                  : "text-text-tertiary"
              }`}
            >
              <Search size={14} />
            </button>
            <PinThreadButton
              messages={activeConvo.messages}
              sessionId={activeConvo.sessionId}
              alreadyPinned={Boolean(activeConvo.pinnedThreadId)}
              onPinned={handlePinned}
            />
          </div>
        </div>
      </header>

      {/* In-session history search (M25.6) */}
      {searchOpen && (
        <div className="border-b border-border-subtle px-3 py-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("app.chat_search_conv_placeholder")}
            aria-label={t("app.cp_search_conv_aria")}
            autoFocus
            className="w-full rounded-md border border-border-subtle bg-surface-sunken px-2.5 py-1 text-xs text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-ai/40"
          />
          {query && (
            <p className="mt-1 text-xs text-text-tertiary">
              {t("app.chat_search_match_summary", {
                shown: visibleMessages.length,
                total: activeConvo.messages.length,
              })}
            </p>
          )}
        </div>
      )}

      {/* Message history */}
      <div
        ref={scrollRef}
        className="flex-1 space-y-4 overflow-y-auto px-4 py-4"
      >
        {activeConvo.messages.length === 0 ? (
          <div className="flex flex-col gap-3 pt-1">
            {/* Greeting card — brand-blue ai-bg, today's date,
                then the context-aware suggested questions. */}
            <div className="rounded-2xl border border-ai-subtle bg-ai-bg p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
                {new Date().toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-text-primary">
                <AIBadge />
                How can I help today?
              </p>
              <p className="mt-1 text-xs leading-relaxed text-text-secondary">
                I can query records, explain calculations, draft
                documents, and make configuration changes — every
                change goes through a confirmation card first.
              </p>
            </div>
            <SuggestedActions
              currentUrl={
                typeof window !== "undefined"
                  ? window.location.pathname
                  : "/"
              }
              onPick={(p) => void send(p)}
            />
          </div>
        ) : visibleMessages.length === 0 ? (
          <p className="pt-6 text-center text-xs text-text-tertiary">
            {t("app.cp_no_match", { q: search.trim() })}
          </p>
        ) : (
          visibleMessages.map((turn) => (
            <Fragment key={turn.id}>
              {/* Time divider above each user turn — marks a new
                  message group (Phase 4.3). */}
              {turn.role === "user" && (
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-text-tertiary">
                  <span className="h-px flex-1 bg-border-subtle" />
                  {_timeLabel(turn.createdAt)}
                  <span className="h-px flex-1 bg-border-subtle" />
                </div>
              )}
              <ChatMessage
                turn={turn}
                sessionId={activeConvo.sessionId}
                onSendMessage={(t) => void send(t)}
                onSaveDraft={(draft) => {
                  void api.saveChatDraft({
                    ...draft,
                    session_id: activeConvo.sessionId,
                  });
                }}
                onApplyConfirmation={(id) =>
                  void applyConfirmation(id)
                }
                onCancelConfirmation={(id) =>
                  void cancelConfirmation(id)
                }
              />
            </Fragment>
          ))
        )}
      </div>

      {/* Input */}
      <ChatInput onSend={(t) => void send(t)} disabled={streaming} />
      </div>
    </aside>
  );
}
